import { useEffect } from "react";

import { LogSentryException, ServiceError, SpinnerCenter } from "@shapr3d/frontend-lib";
import { Navigate, useSearchParams } from "react-router-dom";

import { useSSOCallback } from "../hooks/useAuth";
import { SearchParam, ServiceErrorCode } from "../types";
import { PageSource } from "../types/tracking";
import { sanitizeRedirectUrl } from "../urlUtils";

export const SSOCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const code = searchParams.get(SearchParam.SSOCode);
  const state = searchParams.get(SearchParam.SSOState);
  const error = searchParams.get(SearchParam.SSOError);
  const errorDescription = searchParams.get(SearchParam.SSOErrorDescription);

  useEffect(() => {
    if (error || errorDescription) {
      LogSentryException({ messagePrefix: "SSO error", error, extra: { errorDescription } });
    }
  }, [error, errorDescription]);

  const {
    data: SSOCallbackData,
    mutate: SSOCallbackMutation,
    error: SSOCallbackError,
    isPending: isSSOCallbackPending,
    isIdle: isSSOCallbackIdle,
  } = useSSOCallback();

  useEffect(() => {
    if (!code || !state) {
      return;
    }

    SSOCallbackMutation({ code, authSessionID: state });
  }, [code, state, SSOCallbackMutation]);

  if (!code || !state || error || errorDescription) {
    if (error === "profile_not_allowed_outside_organization") {
      return <Navigate to="/sso/mismatch" replace />;
    }

    return <Navigate to="/sso/error" replace />;
  }

  if (isSSOCallbackIdle || isSSOCallbackPending) {
    return <SpinnerCenter />;
  }

  if (SSOCallbackError) {
    if (SSOCallbackError instanceof ServiceError) {
      if (SSOCallbackError.hasCode(ServiceErrorCode.SSOAuthenticationEmailMismatch)) {
        return <Navigate to="/sso/mismatch" replace />;
      }

      if (SSOCallbackError.hasCode(ServiceErrorCode.SSOAuthenticationSessionExpired)) {
        return <Navigate to="/sso/timeout" replace />;
      }
    }

    return <Navigate to="/sso/error" replace />;
  }

  if (SSOCallbackData?.redirectURL) {
    const decodedURI = decodeURIComponent(SSOCallbackData.redirectURL);
    if (decodedURI.startsWith("/")) {
      const [path, search] = decodedURI.split("?");
      const searchParams = new URLSearchParams(search);
      searchParams.set(SearchParam.Source, PageSource.SSOCallback);

      return <Navigate to={`${path}?${searchParams.toString()}`} replace={true} />;
    }

    const redirectURL = sanitizeRedirectUrl(decodedURI);
    redirectURL.searchParams.set(SearchParam.Source, PageSource.SSOCallback);

    window.location.replace(redirectURL.toString());
    return null;
  }

  if (SSOCallbackData?.teamID) {
    return <Navigate to={`/teams/${SSOCallbackData.teamID}`} replace={true} />;
  }

  return <Navigate to="/" replace />;
};
